import * as React from 'react';
import { useState, useEffect } from 'react';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useLocation, Link } from 'react-router-dom';
import {
  StyledListItemButtonParent,
  StyledListItemButtonChild,
  StyledListItemIcon,
  StyledListItemText,
  StyledAvatar,
  CompanyLogoContainer,
  StyledMainListItemText,
} from './Sidebar.styles';
import RElogo from '../../Assets/AuthBackGround/RecEzyLogo.png';
import { Box, IconButton } from '@mui/material';
import { theme } from '../../theme';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useParams } from 'react-router-dom';
import { useGlobalContext } from '../../Context/GlobalContext';
import { useNavigate } from 'react-router-dom';

export default function Sidebar() {
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(null);
  const { tenant = 'defaultTenant' } = useParams<{ tenant: string }>();
  const [activeChildPath, setActiveChildPath] = useState<string>(`/${tenant}`);
  const [lastActiveParent, setLastActiveParent] = useState<string | null>(null);
  const [isExpanded, setExpanded] = useState<boolean>(false)
  const [isCurrentPath, setCurrentPath] = useState<string | null>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const { userData } = useGlobalContext();
  const navigate = useNavigate();


  const routeToParentMap: Record<string, string> = {};

  const tenantRoutes = [
    { route: `/${tenant}/Joblisting`, parent: 'jobManagement' },
    { route: `/${tenant}/ImportProfiles`, parent: 'jobManagement' },
    { route: `/${tenant}/hiringpipeline`, parent: 'candidateManagement' },
    // { route: `/${tenant}/AssignTask`, parent: 'candidateManagement' },
    { route: `/${tenant}/ImportNewJob`, parent: 'internalJobs' },
    { route: `/${tenant}/CandidateDetails`, parent: 'preboarding' },
    { route: `/${tenant}/Dashboard`, parent: 'insights' },
    { route: `/${tenant}/Users`, parent: 'admin' },
    { route: `/${tenant}/Communication`, parent: 'admin' },
    { route: `/${tenant}/PanelStakeholders`, parent: 'admin' }, // Added Panel Stakeholders route
    {route :`/${tenant}/preboarding-configuration`,parent:'admin'}
  ];


  tenantRoutes.forEach(({ route, parent }) => {
    routeToParentMap[`/${tenant}/${route}`] = parent;
  });

  const handleClick = (section: string) => {
    const firstChild = getChildItems(section, tenant)[0];
  
    if (open !== section) {
      setLastActiveParent(isCurrentPath); // Update last active parent
      setOpen(section);                   // Expand the clicked parent
    }
  
    if (firstChild) {
      navigate(firstChild.path);          // Always navigate to the first child
    }
  };
  

  // outside click listener to collapse the sidebar.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this useEffect inside your Sidebar component (e.g., below your location-based useEffect)
  useEffect(() => {
    // When the sidebar is collapsed, force open to be the parent of the active child.
    if (!isExpanded && activeChildPath) {
      const parent = routeToParentMap[activeChildPath];
      if (parent && parent !== open) {
        setOpen(parent);
      }
    }
  }, [isExpanded, activeChildPath, open]);

  useEffect(() => {
    if (!tenant) return;

    const currentPath = location.pathname.toLowerCase();
    let bestMatch = null;
    let bestSection = null;
    let bestLength = 0;
    const allSections = ['jobManagement', 'candidateManagement', 'preboarding', 'admin'];
    for (const section of allSections) {
      const childItems = getChildItems(section, tenant);
      for (const item of childItems) {
        const itemPath = item.path.toLowerCase();
        // Prefer exact match, but if multiple matches, prefer the longest
        if (currentPath === itemPath && itemPath.length > bestLength) {
          bestMatch = item;
          bestSection = section;
          bestLength = itemPath.length;
        }
      }
    }

    if (bestMatch) {
      setActiveChildPath(bestMatch.path);
      setOpen(bestSection);
      setCurrentPath(bestSection);
      setLastActiveParent(bestSection);
    } else {
      // Fallback: longest prefix match
      let fallbackMatch = null;
      let fallbackSection = null;
      let fallbackLength = 0;
      for (const section of allSections) {
        const childItems = getChildItems(section, tenant);
        for (const item of childItems) {
          const itemPath = item.path.toLowerCase();
          if (currentPath.startsWith(itemPath) && itemPath.length > fallbackLength) {
            fallbackMatch = item;
            fallbackSection = section;
            fallbackLength = itemPath.length;
          }
        }
      }
      if (fallbackMatch) {
        setActiveChildPath(fallbackMatch.path);
        setOpen(fallbackSection);
        setCurrentPath(fallbackSection);
        setLastActiveParent(fallbackSection);
      } else {
        // If no fallback, use first jobManagement child
        const defaultSection = 'jobManagement';
        const defaultChild = getChildItems(defaultSection, tenant)[0];
        if (defaultChild) {
          setActiveChildPath(defaultChild.path);
          setOpen(defaultSection);
          setCurrentPath(defaultSection);
          setLastActiveParent(defaultSection);
        }
      }
    }
  }, [location.pathname, tenant]);



  // Function to handle mouse leave events with a delay
  const handleExpandCollapse = () => {
    setExpanded((prev) => !prev);
  };


  return (
    <div
      ref={sidebarRef}
      style={{
        position: 'absolute',
        left: '-1vw',
        paddingLeft: '1vw',
        transition: 'width 0.3s ease-in-out, padding-left 0.3s ease-in-out',
        width: isExpanded ? '100%' : '4.5vw',
        height: '100vh',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        zIndex: 1000,
      }}
      onClick={() => { if (!isExpanded) setExpanded(true); }}
    >
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isExpanded
            ? {
              xs: '20vw',
              sm: '20vw',
              md: '20vw',
              lg: '20vw',
              xl: '18vw',
            }
            : '5vw',
          height: '100vh',
          maxWidth: '20vw',
          bgcolor: theme.palette.secondary.light,
          borderRight: '1px solid #E4E7EC',
          position: 'fixed',
          transition: 'width 0.3s ease-in-out',
        }}
        component="nav"
        aria-labelledby="nested-list-subheader"
      >
        <Box sx={{ width: '2.5vw', height: '2.5vw',display: 'flex', alignItems: 'center', justifyContent: 'center',   pointerEvents: "auto" }}>
          <IconButton
            onClick={handleExpandCollapse}
            sx={{
              border: '1px solid #e0e0e0',
              transition: 'transform 0.3s ease, left 0.3s ease',
              position: 'absolute',
              left: isExpanded ? '15vw' : '1.5vw', // Moves the button smoothly
            }}
          >
            {/* {isExpanded? <i className="fa-solid fa-angles-left"></i>:<></>} */}
            <i
              className={`fa-solid ${isExpanded ? 'fa-solid fa-angles-right' : 'fa-list-ul'}`}
              style={{ transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
            ></i>
          </IconButton>

        </Box>
        {/* Group A: Company Logo */}
        <CompanyLogoContainer sx={{ bgcolor: 'transparent', paddingLeft: isExpanded ? '2vw' : '0.8vw', display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ width: '3.5vw', height: '3.5vw', ml: '0.28vw', mt: '0.2vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StyledAvatar sx={{ width: '2.5vw', height: '2.5vw' }} src={RElogo} />
          </Box>
          {isExpanded && (
            <Typography
              variant="h5"
              sx={{
                position: 'relative',
                top: '0.1vh',
                ml: 0,
                bgcolor: theme.palette.secondary.light,
                display: 'inline-block',
                overflow: 'hidden',
                whiteSpace: 'nowrap', // Prevents text from wrapping
                // width: '0', // Start with width of 0
                animation: 'typing 0.3s steps(10) 0.1s 0.5 normal both', // Typing animation with 0.3s duration
                // animationFillMode: 'forwards', // Keep the final state after animation
                visibility: 'visible', // Ensure the text is visible for typing animation
              }}
            >
              RecEzy
            </Typography>)}
        </CompanyLogoContainer>


        {/* Group B: Main Menu (Scrollable) */}
        <Box
          sx={{
            flex: 1,
            width: isExpanded ? '100%' : '5vw',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {['jobManagement',
            //  'ekoRecruit', 
            'candidateManagement',
            //  'internalJobs', 
            'preboarding',
            //  'insights'
          ].map(section => (
            <React.Fragment key={section}>
              <StyledListItemButtonParent
                onClick={() => handleClick(section)}
                sx={{
                  '@media (max-width:940px)': {
                    width: isExpanded ? '90%' : '3.8vw',
                    height: isExpanded ? '5vh' : '3.2vw',
                    position: 'relative',
                    left: '-2px',
                  },
                  '@media (min-width:940px)': {
                    width: isExpanded ? '90%' : '3.2vw',
                    height: isExpanded ? '5vh' : '3.2vw',
                  },

                  '@media (min-width:1344px)': {
                    width: isExpanded ? '90%' : '3vw',
                    height: isExpanded ? '5vh' : '3vw',
                  },
                  backgroundColor: open === section ? theme.palette.primary.main : 'transparent',
                  color: open === section ? theme.palette.secondary.light : theme.palette.primary.main,
                  transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                  '& .MuiListItemText-primary': {
                    color: open === section ? theme.palette.secondary.light : theme.palette.primary.main,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main, // Ensure background changes on hover without affecting text
                    '& .MuiListItemText-primary': { color: '#fff', },
                    color: theme.palette.secondary.light,

                  },
                }}
              >
                {/* Icon Container */}
                <StyledListItemIcon
                  sx={{
                    color: 'inherit',
                    ":hover": { color: '#fff' },
                    transition: 'width 0.3s ease-in-out',
                  }}
                >
                  <i
                    style={{
                      position: 'relative',
                      paddingRight: '1vw',
                      left: isExpanded ? '-0.3vw' : '0vw',
                      fontSize: isExpanded ? '1.2rem' : '1.5rem',

                      transition: 'left 0.3s ease, font-size 0.3s ease',
                    }}
                    className={getIconClass(section)}
                  />
                </StyledListItemIcon>

                {/* Text and Expand/Collapse Icon */}
                {isExpanded && (
                  <StyledMainListItemText
                    sx={{
                      color: theme.palette.primary.main,

                      position: 'relative',
                      top: '0.1vh',

                      ml: '0.5rem',
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: 'fit-content',
                      animation: 'typing 4s steps(40) 1s 1 normal both',
                      animationFillMode: 'forwards',
                    }}
                    primary={getDisplayName(section)}
                  />
                )}
                {isExpanded && (
                  open === section ? (
                    <i style={{ position: 'relative', right: '0.5vw' }} className="fa-solid fa-chevron-down" />
                  ) : (
                    <i style={{ position: 'relative', right: '0.5vw' }} className="fa-solid fa-chevron-up" />
                  )
                )}
              </StyledListItemButtonParent>


              {isExpanded && <Collapse in={open === section} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {getChildItems(section, tenant).map(child => {
                    const normalizedPath = child.path.toLowerCase(); // use full path

                    return (<Link
                      key={child.path}
                      to={child.path}
                      style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                      onClick={() => setExpanded(false)}
                    >
                      <StyledListItemButtonChild
                        sx={{
                          pl: '2vw',
                          transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                          backgroundColor: activeChildPath.toLowerCase() === normalizedPath ? '#F4EBFF' : 'transparent',
                          color: activeChildPath.toLowerCase() === normalizedPath ? theme.palette.primary.main : theme.palette.primary.main, // Keep child text always visible with primary.main
                        }}
                      >
                        {isExpanded && (
                          <StyledListItemText
                            primary={child.name}
                            sx={{
                              backgroundColor: 'transparent',
                              color: activeChildPath.toLowerCase() === normalizedPath ? theme.palette.primary.main : 'inherit', // Ensure child text color remains correct
                            }}
                          />
                        )}
                      </StyledListItemButtonChild>

                    </Link>)
                  })}
                </List>
              </Collapse>}
            </React.Fragment>
          ))}
          {(userData.UserRole.toLowerCase() === 'super admin' || userData.UserRole.toLowerCase() === 'admin') && (
            <React.Fragment>
              <StyledListItemButtonParent
                onClick={() => handleClick('admin')}
                sx={{
                  backgroundColor: open === 'admin' ? theme.palette.primary.main : 'transparent',
                  color: open === 'admin' ? '#FFFFFF' : theme.palette.primary.main,
                  '@media (max-width:1820px)': {
                    width: isExpanded ? '90%' : '3.2vw',
                    height: isExpanded ? '5vh' : '3.2vw',
                    left: '-2px',
                  },

                  '@media (min-width:1820px)': {
                    width: isExpanded ? '90%' : '2vw',
                    height: isExpanded ? '5vh' : '2vw',
                  },
                  '@media (min-width:1344px)': {
                    width: isExpanded ? '90%' : '3vw',
                    height: isExpanded ? '5vh' : '3vw',
                  },
                  transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                  '& .MuiListItemText-primary': {
                    color: open === 'admin' ? theme.palette.secondary.light : theme.palette.primary.main,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main, // Ensure background changes on hover without affecting text
                    '& .MuiListItemText-primary': { color: '#fff', },
                    color: theme.palette.secondary.light,

                  },

                }}
              >

                <StyledListItemIcon
                  sx={{
                    color: 'inherit',
                    ":hover": { color: '#fff' },
                    transition: 'width 0.3s ease-in-out',
                  }}>
                  <i style={{
                    position: 'relative',
                    paddingRight: '1vw',
                    left: isExpanded ? '-0.3vw' : '0vw',
                    fontSize: "admin" === 'admin' ? (isExpanded ? '1.1rem' : '1.2rem') : (isExpanded ? '1.2rem' : '1.5rem'),

                    transition: 'left 0.3s ease, font-size 0.3s ease',
                  }} className={getIconClass("admin")} />
                </StyledListItemIcon>
                {isExpanded && (<StyledMainListItemText sx={{
                  color: theme.palette.primary.main,

                  position: 'relative',
                  top: '0.1vh',

                  ml: 'admin' === 'admin' ? '0.5rem' : '0.6rem',
                  display: 'inline-block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: 'fit-content',
                  animation: 'typing 4s steps(40) 1s 1 normal both',
                  animationFillMode: 'forwards',
                }}
                  primary={getDisplayName('admin')} />)}
                {isExpanded && (
                  open === 'admin' ? (
                    <i style={{ position: 'relative', right: '0.5vw' }} className="fa-solid fa-chevron-down" />
                  ) : (
                    <i style={{ position: 'relative', right: '0.5vw' }} className="fa-solid fa-chevron-up" />
                  )
                )}

              </StyledListItemButtonParent>
              <Collapse in={open === 'admin'} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {getChildItems('admin', tenant).map(child => {
                    const normalizedPath = child.path.toLowerCase(); // use full path

                    return (<Link
                      key={child.path}
                      to={child.path}
                      style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                      onClick={() => setExpanded(false)}
                    >
                      {isExpanded && (<StyledListItemButtonChild
                        sx={{
                          pl: '2vw',
                          backgroundColor: activeChildPath.toLowerCase() === normalizedPath ? '#F4EBFF' : 'transparent',
                          color: activeChildPath.toLowerCase() === normalizedPath
                            ? theme.palette.primary.main
                            : '#000000',
                        }}
                      >
                        <StyledListItemText primary={child.name} />
                      </StyledListItemButtonChild>)}
                    </Link>
                    )
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          )}
        </Box>
      </List>
    </div>
  );
}

// Helper Functions
const getIconClass = (section: string) => {
  const icons: Record<string, string> = {
    jobManagement: 'fa-solid fa-briefcase fa-fw',
    candidateManagement: 'fa-solid fa-users fa-fw',          // Represents group of candidates
    preboarding: 'fa-solid fa-clipboard-user fa-fw',       // Represents pre-onboarding checklist
    admin: 'fa-solid fa-user-gear fa-fw',
  };
  return icons[section];
};





const getDisplayName = (section: string) => {
  const displayNames: Record<string, string> = {
    jobManagement: 'Job Management',
    // ekoRecruit: 'Eko Recruit',
    candidateManagement: 'Candidate Management',
    // internalJobs: 'Internal Jobs',
    preboarding: 'Preboarding',
    // insights: 'Insights',
    admin: 'Admin',
  };
  return displayNames[section];
};

const getChildItems = (section: string, tenant: string) => {
  if (!tenant) {
    // Handle the case where tenant is undefined, return an empty array or throw an error
    return [];
  }
  const childItems: Record<string, { path: string; name: string }[]> = {
    jobManagement: [
      { path: `/${tenant}/Joblisting`, name: 'Job Listing' },
      { path: `/${tenant}/ImportProfiles`, name: 'Import Profiles' },
      // { path: `/${tenant}/Askekotocreateajobposting`, name: 'Ask Eko to Create a Job Posting' },
      // { path: `/${tenant}/Importjobs`, name: 'Import Jobs' },
    ],
    // ekoRecruit: [{ path: `/${tenant}/EkoMatching`, name: 'Eko Matching' }],
    candidateManagement: [
      { path: `/${tenant}/hiringpipeline`, name: 'Hiring Pipeline' },
      // { path: `/${tenant}/AssignTask`, name: 'Assign Task' },
    ],
    // internalJobs: [{ path: `/${tenant}/ImportNewJob`, name: 'Import New Job' }],
    preboarding: [
      // { path: `/${tenant}/CandidateDetails`, name: 'Candidate Details' },
      {path:`/${tenant}/preboarding`,name:'Preboarding'}
    ],
    // insights: [
    //   { path: `/${tenant}/Dashboard`, name: 'Dashboard' },
    //   { path: `/${tenant}/Reports`, name: 'Reports' },
    // ],
    admin: [
      { path: `/${tenant}/Users`, name: 'User Management' },
      { path: `/${tenant}/Communication`, name: 'Communication' },
      {path: `/${tenant}/PanelStakeholders`, name: 'Panel & Stakeholder Setup'},
      {path:`/${tenant}/preboarding-configuration`,name:'Preboarding Configuration'}
    ],
  };
  return childItems[section] || [];
};
